import React from "react";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Fade from "@mui/material/Fade";
import MDSnackbar from "components/MDSnackbar";
import { useState } from "react";
import { FormControl, InputLabel } from "@mui/material";
import appData from "layouts/categories/data/appData";
import { useAddCategory, useRefetchCategories } from "api/useCategories.ts";
import MDBox from "components/MDBox";
import { useMediaQuery, useTheme } from "@mui/material";
import { Box } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CircularProgress from "@mui/material/CircularProgress";

function Tables() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const addCategory = useAddCategory();
  const { refetch } = useRefetchCategories();
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: {
      ar: "",
      en: "",
    },
    description: {
      ar: "",
      en: "",
    },
    minSupplyQuantity: 0,
    allowedMosques: "",
    state: "",
    image: "https://res.cloudinary.com/dxaxyz62w/image/upload/v1740483339/hajj_x6ujps.png",
    icon: "https://res.cloudinary.com/dxaxyz62w/image/upload/v1740483339/hajj_x6ujps.png",
    video:
      "https://res.cloudinary.com/dgljetjdr/video/upload/v1740588185/Screen_Recording_2025-02-26_163931_dw5jma.mp4",
  });

  const {
    columns,
    rows,
    editedCategory,
    handleCloseModal,
    isModalOpen,
    notification,
    setNotification,
    handleSaveChanges,
    handleCloseNotification,
    handleInputChange,
    handleDeleteCategory,
    isDeleteModalOpen,
    closeDeleteModal,
    handleUploadImage: originalHandleUploadImage,
    handleFileChange,
    isFileModalOpen,
    closeFileModal,
    imagePreview,
    setImagePreview,
    isIcon,
    isVideo,
  } = appData();
  const handleUploadImage = async () => {
    setIsUploading(true);
    try {
      await originalHandleUploadImage();
      setImagePreview(null);
    } catch (error) {
      console.error("Error uploading image:", error);
      setNotification({
        open: true,
        message: t("categories.imageUploadFailed"),
        severity: "error",
      });
    } finally {
      setIsUploading(false);
      refetch(); // Refetch categories after upload
    }
  };

  const handleAddCategoryClick = () => {
    setIsCategoryModalOpen(true); // Open modal for adding new category
  };

  const handleCloseAddCategoryModal = () => {
    setIsCategoryModalOpen(false); // Close modal
    setNewCategory({
      name: {
        en: "",
        ar: "",
      },
      description: {
        en: "",
        ar: "",
      },
      minSupplyQuantity: 0,
      allowedMosques: "",
      state: "",
    });
  };

  const handleInputChangeNewCategory = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parentKey, childKey] = name.split(".");
      setNewCategory((prevState) => ({
        ...prevState,
        [parentKey]: {
          ...prevState[parentKey],
          [childKey]: value,
        },
      }));
    } else {
      setNewCategory((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  const handleSaveCategory = async () => {
    const categoryData = {
      name: {
        en: newCategory.name.en,
        ar: newCategory.name.ar,
      },
      description: {
        en: newCategory.description.en,
        ar: newCategory.description.ar,
      },
      minSupplyQuantity: newCategory.minSupplyQuantity,
      image: newCategory.image,
      icon: newCategory.icon,
      video: newCategory.video,
      state: newCategory.state,
      allowedMosques: newCategory.allowedMosques,
    };

    addCategory.mutate(categoryData, {
      onSuccess: () => {
        setNotification({
          open: true,
          message: t("categories.categoryAdded"),
          severity: "success",
        });
        handleCloseAddCategoryModal(); // Close modal on success
      },
      onError: (error) => {
        console.error("Error adding category:", error);
        setNotification({
          open: true,
          message: t("categories.categoryAddFailed"),
          severity: "error",
        });
      },
    });
  };
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="dark"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                justifyContent="space-between"
                alignItems={isMobile ? "flex-start" : "center"}
              >
                <MDTypography variant="h6" color="white">
                  {t("categories.title")}
                </MDTypography>
                <MDBox
                  display="flex"
                  flexDirection={isMobile ? "column" : "row"}
                  gap={isMobile ? 2 : "10px"}
                  width={isMobile ? "100%" : "auto"}
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleAddCategoryClick}
                    fullWidth={isMobile}
                    sx={{
                      borderRadius: 2,
                      fontSize: 14,
                      padding: "8px 16px",
                      color: "black",
                    }}
                  >
                    {t("categories.addCategory")}
                  </Button>
                </MDBox>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={true}
                  entriesPerPage={false}
                  showTotalEntries
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      {/* Category Modal */}
      <Dialog open={isCategoryModalOpen} onClose={handleCloseAddCategoryModal}>
        <DialogTitle>{t("categories.addNewCategory")}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              {/* Category Name (English) */}
              <TextField
                name="name.en"
                label={t("categories.fields.categoryNameEn")}
                value={newCategory.name.en}
                onChange={handleInputChangeNewCategory}
                fullWidth
                margin="dense"
              />
            </Grid>
            <Grid item xs={6}>
              {/* Category Name (Arabic) */}
              <TextField
                name="name.ar"
                label={t("categories.fields.categoryNameAr")}
                value={newCategory.name.ar}
                onChange={handleInputChangeNewCategory}
                fullWidth
                margin="dense"
                dir="rtl" // Right-to-left direction for Arabic
              />
            </Grid>
          </Grid>
          {/* Description (English) */}
          <TextField
            name="description.en"
            label={t("categories.fields.descriptionEn")}
            value={newCategory.description.en}
            onChange={handleInputChangeNewCategory}
            fullWidth
            margin="dense"
            multiline
            rows={4}
          />
          {/* Description (Arabic) */}
          <TextField
            name="description.ar"
            label={t("categories.fields.descriptionAr")}
            value={newCategory.description.ar}
            onChange={handleInputChangeNewCategory}
            fullWidth
            margin="dense"
            multiline
            rows={4}
            dir="rtl" // Right-to-left direction for Arabic
          />

          {/* Min Supply Quantity */}
          <TextField
            name="minSupplyQuantity"
            label={t("categories.fields.minSupplyQuantity")}
            value={newCategory.minSupplyQuantity}
            onChange={handleInputChangeNewCategory}
            type="number"
            inputProps={{ min: 0, step: 1 }}
            fullWidth
            margin="dense"
            helperText={t("categories.fields.minSupplyQuantityHelp")}
          />

          {/* Allowed Mosques */}
          <FormControl fullWidth margin="dense">
            <InputLabel id="new-category-allowed-label">
              {t("categories.fields.allowedMosques")}
            </InputLabel>
            <Select
              labelId="new-category-allowed-label"
              id="new-category-allowed-mosques"
              name="allowedMosques"
              value={newCategory.allowedMosques || ""}
              label={t("categories.fields.allowedMosques")}
              onChange={handleInputChangeNewCategory}
              sx={{ height: 56 }}
            >
              <MenuItem value="haram">{t("categories.options.haram")}</MenuItem>
              <MenuItem value="haram_and_mosques">
                {t("categories.options.haramAndMosques")}
              </MenuItem>
              <MenuItem value="mosques">{t("categories.options.mosques")}</MenuItem>
            </Select>
          </FormControl>

          {/* Category State */}
          <FormControl fullWidth margin="dense">
            <InputLabel id="new-category-state-label">
              {t("categories.fields.categoryState")}
            </InputLabel>
            <Select
              labelId="new-category-state-label"
              id="new-category-state"
              name="state"
              value={newCategory.state || ""}
              label={t("categories.fields.categoryState")}
              onChange={handleInputChangeNewCategory}
              sx={{ height: 56 }}
            >
              <MenuItem value="Available">{t("categories.states.available")}</MenuItem>
              <MenuItem value="Test">{t("categories.states.test")}</MenuItem>
              <MenuItem value="Hidden">{t("categories.states.hidden")}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddCategoryModal} color="secondary">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSaveCategory} color="primary">
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit Category Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 500 }}
      >
        <DialogTitle>{t("categories.editCategory")}</DialogTitle>
        {editedCategory && (
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  name="name.en"
                  label={t("categories.fields.categoryNameEn")}
                  value={editedCategory.name.en}
                  onChange={handleInputChange}
                  fullWidth
                  margin="dense"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="name.ar"
                  label={t("categories.fields.categoryNameAr")}
                  value={editedCategory.name.ar}
                  onChange={handleInputChange}
                  fullWidth
                  margin="dense"
                  dir="rtl"
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <TextField
                  name="description.en"
                  label={t("categories.fields.descriptionEn")}
                  value={editedCategory.description.en}
                  onChange={handleInputChange}
                  fullWidth
                  margin="dense"
                  multiline
                  rows={4}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="description.ar"
                  label={t("categories.fields.descriptionAr")}
                  value={editedCategory.description.ar}
                  onChange={handleInputChange}
                  fullWidth
                  margin="dense"
                  multiline
                  rows={4}
                  dir="rtl"
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="category-type-label">
                    {t("categories.fields.allowedMosques")}
                  </InputLabel>
                  <Select
                    labelId="category-allowed-label"
                    id="category-allowed-mosques"
                    name="allowedMosques"
                    value={editedCategory.allowedMosques}
                    label={t("categories.fields.allowedMosques")}
                    onChange={handleInputChange}
                    disabled={isUploading}
                    sx={{ height: 56 }}
                  >
                    <MenuItem value="haram">{t("categories.options.haram")}</MenuItem>
                    <MenuItem value="haram_and_mosques">
                      {t("categories.options.haramAndMosques")}
                    </MenuItem>
                    <MenuItem value="mosques">{t("categories.options.mosques")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="category-state-label">
                    {t("categories.fields.categoryState")}
                  </InputLabel>
                  <Select
                    labelId="category-state-label"
                    id="category-state"
                    name="state"
                    value={editedCategory.state}
                    label={t("categories.fields.categoryState")}
                    onChange={handleInputChange}
                    disabled={isUploading}
                    sx={{ height: 56 }}
                  >
                    <MenuItem value="Available">{t("categories.states.available")}</MenuItem>
                    <MenuItem value="Test">{t("categories.states.test")}</MenuItem>
                    <MenuItem value="Hidden">{t("categories.states.hidden")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Min Supply Quantity */}
            <TextField
              name="minSupplyQuantity"
              label={t("categories.fields.minSupplyQuantity")}
              type="number"
              value={editedCategory.minSupplyQuantity || 0}
              onChange={handleInputChange}
              inputProps={{ min: 0, step: 1 }}
              fullWidth
              margin="dense"
              helperText={t("categories.fields.minSupplyQuantityHelp")}
            />
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            {t("common.cancel")}
          </Button>
          <Button onClick={() => handleSaveChanges(editedCategory)} color="primary">
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirm Deletion */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={closeDeleteModal}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 500 }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("categories.confirmDeletion")}</DialogTitle>
        <DialogContent>
          <MDTypography variant="body1" color="textSecondary">
            {t("categories.deleteCategoryConfirm")}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteModal} color="secondary">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleDeleteCategory} color="primary">
            {t("common.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* File Modal */}
      <Dialog
        open={isFileModalOpen}
        onClose={closeFileModal}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 500 }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {t("categories.editCategoryMedia", {
            type: isVideo
              ? t("categories.media.video")
              : isIcon
              ? t("categories.media.icon")
              : t("categories.media.image"),
          })}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            {/* Preview Section */}
            {imagePreview && (
              <Box
                sx={{
                  width: "100%",
                  height: "auto",
                  maxHeight: 300,
                  borderRadius: 2,
                  border: "1px solid #ccc",
                }}
              >
                {isVideo ? (
                  <video
                    controls
                    src={imagePreview}
                    style={{
                      width: "100%",
                      height: "auto",
                      maxHeight: 300,
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Category Preview"
                    sx={{
                      width: "100%",
                      height: "auto",
                      maxHeight: 300,
                      objectFit: "contain",
                    }}
                  />
                )}
              </Box>
            )}
            <Button
              component="label"
              tabIndex={-1}
              startIcon={<CloudUploadIcon />}
              fullWidth
              disabled={isUploading}
            >
              {t("categories.uploadCategoryMedia", {
                type: isVideo
                  ? t("categories.media.video")
                  : isIcon
                  ? t("categories.media.icon")
                  : t("categories.media.image"),
              })}
              <input
                hidden
                accept={isVideo ? "video/*" : "image/*"}
                type="file"
                name={isVideo ? "video" : "image"}
                onChange={handleFileChange}
                disabled={isUploading}
              />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFileModal} color="secondary" disabled={isUploading}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleUploadImage}
            color="primary"
            disabled={isUploading || !imagePreview}
            startIcon={isUploading ? <CircularProgress size={20} /> : null}
          >
            {isUploading ? t("common.uploading") : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <MDSnackbar
        color={notification.severity}
        icon={notification.severity === "success" ? "check" : "error"}
        title={notification.severity === "success" ? t("common.success") : t("common.error")}
        content={notification.message}
        open={notification.open}
        onClose={handleCloseNotification}
        close={handleCloseNotification}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default Tables;
